export interface PptxDeckSlideInfo {
	slideIndex: number; // 1-indexed
	totalSteps: number; // >= 1
}

export interface PptxDeckPosition {
	slide: number; // 1-indexed
	step: number; // 1-indexed within the slide
}

export type PptxDeckReconcilePlan =
	| { type: 'noop' }
	| { type: 'step'; commands: ('NextStep' | 'PreviousStep')[] }
	| { type: 'reload'; slideNumber: number; thenSteps: number };

// The viewer's `totalSteps` is the build count for the slide; `CurrentStep`
// echoes range 0..totalSteps inclusive (base state + each build), so the
// slide has totalSteps + 1 internal (1-indexed) step values. Verified against
// a live deck: a slide reporting `totalSteps:4` echoed `CurrentStep:4` at its
// final build.
/** Last valid 1-indexed step in a slide (defaults to 1 for unknown slides). */
export const pptxDeckTotalSteps = (slides: PptxDeckSlideInfo[], slide: number): number => {
	const found = slides.find((s) => s.slideIndex === slide);
	return found ? found.totalSteps + 1 : 1;
};

/** True when the slide has at least one build — a static slide reports totalSteps:1. */
export const pptxDeckSlideHasBuilds = (slides: PptxDeckSlideInfo[], slide: number): boolean => (
	(slides.find((s) => s.slideIndex === slide)?.totalSteps ?? 0) > 1
);

/** 1-based global step index: first step of the first slide === 1. */
export const pptxDeckLinearIndex = (
	slides: PptxDeckSlideInfo[],
	slide: number,
	step: number,
): number => {
	const priorSteps = slides
		.filter((s) => s.slideIndex < slide)
		.reduce((acc, s) => acc + s.totalSteps + 1, 0);
	return priorSteps + step;
};

const maxSlide = (slides: PptxDeckSlideInfo[]): number => (
	slides.reduce((max, s) => Math.max(max, s.slideIndex), 0)
);

// `order[displayPosition - 1]` is the physical DOP slideIndex shown at that
// display position (a saved reorder of the deck). `slides` (from the viewer's
// AllInfo) always reports physical order, independent of any reorder, so the
// functions below translate at the two places that cross the boundary: the
// slides array handed to the navigation math above, and the slide number
// posted to / echoed by the iframe. A `null` entry is a page added to the deck
// after conversion (an uploaded image, not part of the DOP document).

/**
 * Remaps `slides` (physical DOP order) into display order, rewriting
 * `slideIndex` to the contiguous 1..N display position — the navigation math
 * above assumes `slideIndex` already matches display order, which only holds
 * after this remap once a deck has been reordered. Identity passthrough when
 * `order` is absent (deck never reordered).
 */
export const pptxDeckToDisplayOrder = (
	slides: PptxDeckSlideInfo[],
	order?: (number | null)[],
): PptxDeckSlideInfo[] => {
	if (!order || order.length === 0 || slides.length === 0) return slides;
	return order.reduce<PptxDeckSlideInfo[]>((acc, physicalIndex) => {
		// An added page is a single static step, so one click moves past it.
		if (physicalIndex === null) {
			acc.push({ slideIndex: acc.length + 1, totalSteps: 0 });
			return acc;
		}
		const found = slides.find((s) => s.slideIndex === physicalIndex);
		// `acc.length + 1`, not the source index, so a transiently-missing entry
		// (e.g. AllInfo still loading) never leaves a gap in the 1..N contiguity
		// the navigation math depends on.
		if (found) acc.push({ slideIndex: acc.length + 1, totalSteps: found.totalSteps });
		return acc;
	}, []);
};

/** Display position -> physical DOP slideIndex. Identity when `order` is absent. */
export const pptxDeckDisplayToPhysical = (
	order: (number | null)[] | undefined,
	displayIndex: number,
): number => order?.[displayIndex - 1] ?? displayIndex;

/** Physical DOP slideIndex -> display position. Identity when `order` is absent. */
export const pptxDeckPhysicalToDisplay = (
	order: (number | null)[] | undefined,
	physicalIndex: number,
): number => {
	if (!order) return physicalIndex;
	const i = order.indexOf(physicalIndex);
	return i === -1 ? physicalIndex : i + 1;
};

/**
 * `media_data.slides` padded (or trimmed) to `pageCount` image_array entries,
 * with `null` for each added page — so it can be moved/spliced in step with
 * image_array, which stays the source of truth for page positions.
 */
export const pptxDeckAlignSlides = <T>(slides: (T | null)[], pageCount: number): (T | null)[] => (
	Array.from({ length: pageCount }, (_, i) => slides[i] ?? null)
);

/** True when the display position is an added page rather than a DOP slide. */
export const pptxDeckIsAddedPage = (
	order: (number | null)[] | undefined,
	displayIndex: number,
): boolean => order?.[displayIndex - 1] === null;

// Navigation math (pptxDeckNextTarget/pptxDeckPrevTarget/pptxDeckLinearIndex) assumes slides
// carry contiguous 1-indexed `slideIndex` values (1..N), as the viewer's AllInfo reports them.
/** Next linear position, or null if already at the end. */
export const pptxDeckNextTarget = (
	slides: PptxDeckSlideInfo[],
	slide: number,
	step: number,
): PptxDeckPosition | null => {
	if (step < pptxDeckTotalSteps(slides, slide)) return { slide, step: step + 1 };
	if (slide < maxSlide(slides)) return { slide: slide + 1, step: 1 };
	return null;
};

/** Previous linear position, or null if already at the start. */
export const pptxDeckPrevTarget = (
	slides: PptxDeckSlideInfo[],
	slide: number,
	step: number,
): PptxDeckPosition | null => {
	if (step > 1) return { slide, step: step - 1 };
	if (slide > 1) return { slide: slide - 1, step: pptxDeckTotalSteps(slides, slide - 1) };
	return null;
};

/**
 * Back-button target for an animated slide: always crosses to the previous
 * slide's own start (step 1) in one click — never retreats through the
 * current slide's builds one at a time, and never lands mid-animation on the
 * slide arrived at. Only resets the current slide in place when there is no
 * previous slide to go to.
 */
export const pptxDeckPrevOrResetTarget = (
	slides: PptxDeckSlideInfo[],
	slide: number,
	step: number,
): PptxDeckPosition | null => {
	if (slide > 1) return { slide: slide - 1, step: 1 };
	if (step > 1) return { slide, step: 1 };
	return null;
};

const stepCommands = (delta: number): ('NextStep' | 'PreviousStep')[] => (
	Array.from({ length: Math.abs(delta) }, () => (delta > 0 ? 'NextStep' : 'PreviousStep'))
);

/**
 * Plan how to drive an iframe currently at `applied` to `target`.
 * - Small linear gaps step (smooth, crosses boundaries).
 * - Large gaps, or cross-slide moves with no AllInfo, reload at the target slide.
 * `K` is the max linear gap that is stepped rather than reloaded.
 */
export const pptxDeckReconcilePlan = (
	applied: PptxDeckPosition,
	target: PptxDeckPosition,
	slides: PptxDeckSlideInfo[],
	K: number,
): PptxDeckReconcilePlan => {
	if (applied.slide === target.slide && applied.step === target.step) return { type: 'noop' };

	// Backing up to a slide's own start (step 1) — whether resetting the
	// current slide in place or crossing to the previous one — is always a
	// clean reload, never a run of PreviousStep commands that would visibly
	// replay builds in reverse or step through the slide being left. A
	// forward move that happens to land on a new slide's first step (e.g.
	// crossing forward at the end of a slide) is unaffected — it's not moving
	// backward, so it keeps the smooth step path.
	if (target.step === 1) {
		const targetLinear = pptxDeckLinearIndex(slides, target.slide, target.step);
		const appliedLinear = pptxDeckLinearIndex(slides, applied.slide, applied.step);
		if (targetLinear < appliedLinear) {
			return { type: 'reload', slideNumber: target.slide, thenSteps: 0 };
		}
	}

	if (slides.length === 0) {
		if (applied.slide === target.slide) {
			return { type: 'step', commands: stepCommands(target.step - applied.step) };
		}
		return { type: 'reload', slideNumber: target.slide, thenSteps: target.step - 1 };
	}

	const delta = pptxDeckLinearIndex(slides, target.slide, target.step)
		- pptxDeckLinearIndex(slides, applied.slide, applied.step);

	if (Math.abs(delta) <= K) return { type: 'step', commands: stepCommands(delta) };
	return { type: 'reload', slideNumber: target.slide, thenSteps: target.step - 1 };
};
